import { Capacitor } from "@capacitor/core";

import { Button, Card, Heading, Stack } from "../ui/primitives.js";

/// Sign-in is delegated to the shared `goastern-auth` service at
/// `auth.goastern.com`. The user clicks "Sign in", lands there for
/// magic-link / Apple SIWA / Google OAuth, and returns to
/// `radar-simulator.goastern.com/` (web) or `goastern-radar://auth/complete`
/// (Capacitor mobile) with a session JWT in the URL fragment.
///
/// On web the worker also sets a `goa_session` cookie at
/// `Domain=.goastern.com` so subsequent fetches authenticate via
/// `credentials: "include"`. Capacitor can't get that cookie (the
/// WebView origin is `https://localhost`); see `authToken.ts` for
/// the Bearer-token fallback the API client uses.
///
/// `onAuthenticated` is called by the parent when `fetchMe()` reports
/// an authenticated user after the redirect — it's no longer driven
/// by an in-page flow.
export interface LoginProps {
  onAuthenticated: (email: string) => void;
}

const AUTH_BASE_URL = "https://auth.goastern.com";
const RETURN_URL = Capacitor.isNativePlatform()
  ? "goastern-radar://auth/complete"
  : `${window.location.origin}/`;

function startSignIn(provider: "magic" | "apple" | "google"): void {
  const params = new URLSearchParams({
    app: "radar",
    returnUrl: RETURN_URL,
  });
  if (provider === "apple") {
    window.location.assign(`${AUTH_BASE_URL}/auth/apple/web/start?${params}`);
  } else if (provider === "google") {
    window.location.assign(`${AUTH_BASE_URL}/auth/google/start?${params}`);
  } else {
    // Magic-link goes through a small interactive form on the
    // goastern-auth side. Best UX is to render that form here, but
    // until we wire a SPA-friendly /auth/magic/request page, the
    // user types their email inline and we POST direct.
    const email = window.prompt("Sign in by email — enter your address:");
    if (email === null || email.trim().length === 0) return;
    void fetch(`${AUTH_BASE_URL}/auth/magic/request`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        app: "radar",
        platform: "web",
        email: email.trim(),
        returnUrl: RETURN_URL,
      }),
    })
      .then((resp) => {
        if (!resp.ok) {
          window.alert(
            `Couldn't send the link (HTTP ${resp.status}). Try again or use Apple / Google sign-in.`,
          );
        } else {
          window.alert(
            `We've sent a sign-in link to ${email}. Click it from this browser to come back signed in.`,
          );
        }
      })
      .catch((err) => {
        window.alert(`Couldn't reach the auth service: ${(err as Error).message}`);
      });
  }
}

// `onAuthenticated` is wired by the parent screen — exported so the
// type stays accurate, even though this component now triggers the
// callback only via the post-redirect path (parent calls fetchMe and
// invokes onAuthenticated). The component itself navigates away
// before any direct call.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function Login(_props: LoginProps): JSX.Element {
  const isNative = Capacitor.isNativePlatform();
  return (
    <Card style={{ width: "100%", maxWidth: 420 }}>
      <Stack gap={20}>
        <Stack gap={6}>
          <Heading level={2}>Sign in</Heading>
          <p style={{ margin: 0, fontSize: 14, color: "var(--text-muted)" }}>
            {isNative
              ? "Sign in to your goastern.com account. Apple, Google, or a one-time email link — whichever you used on this device before."
              : "Sign in to your existing goastern.com account to unlock the full library, classroom mode, and assessment mode."}
          </p>
        </Stack>

        <Stack gap={8}>
          <Button onClick={() => startSignIn("apple")} fullWidth>
            Continue with Apple
          </Button>
          <Button onClick={() => startSignIn("google")} fullWidth variant="secondary">
            Continue with Google
          </Button>
          <Button onClick={() => startSignIn("magic")} fullWidth variant="secondary">
            Sign in with email
          </Button>
        </Stack>

        {!isNative && (
          <Stack
            gap={6}
            style={{
              borderTop: "1px solid var(--border)",
              paddingTop: 16,
              fontSize: 13,
              color: "var(--text-muted)",
            }}
          >
            <strong style={{ color: "var(--text)", fontSize: 14 }}>
              New here?
            </strong>
            <p style={{ margin: 0 }}>
              The radar simulator is bought via the iPad or Android tablet
              app — once you have the paid version on a tablet your sign-in
              works here too. The web build cannot create accounts or take
              payment.
            </p>
            <a
              href="#/store-links"
              style={{
                color: "var(--accent)",
                fontSize: 13,
                textDecoration: "underline",
              }}
            >
              How to get the tablet app →
            </a>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
